import React, { memo, useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'

import { useSelector } from 'react-redux'
import { getHangerState } from '../../../../../store/selectors'

import { Dropdown, FormGroup, FormLabel, RangeSlider } from '../../../../../../../components'

import {
  HANGER_TYPE,
  TOPFLANGE_BEND_MARKS,
  TOPFLANGE_SLOPE_MARKS,
} from '../../../../../common/constants'
import { hanger } from '../../../../../common/json/common.json'
import { isInvalidOffsetDirection } from '../../../../../common/fns'

const TopFlangeOptions = ({ errors, hangerType, onChange, onValidateField }) => {
  const { topFlangeOpenClosedAngle, topFlangeSlopedDownAngle, topFlangeOffset, hlcFlush } =
    useSelector(getHangerState)

  const invalidOffsetDirection = useMemo(
    () => isInvalidOffsetDirection(hangerType, topFlangeOffset),
    [hangerType, topFlangeOffset],
  )

  const handleChangeOpenClosedAngle = useCallback((e) => {
    onValidateField(e)
    onChange({
      [e.target.name]: e.target.value,
    })
  }, [])

  const handleChangeSlopedDownAngle = useCallback((e) => {
    onValidateField(e)
    onChange({
      [e.target.name]: e.target.value,
    })
  }, [])

  const handleChange = useCallback((e) => {
    onChange({
      [e.target.name]: e.target.value,
    })
  }, [])

  return (
    <React.Fragment>
      <div className="grid grid__auto--400 grid--gap-1-2">
        <FormGroup
          className="topFlangeBend"
          warning={hangerType === HANGER_TYPE.ALL_TYPES && parseFloat(topFlangeOpenClosedAngle) !== 0}
        >
          <FormLabel
            htmlFor="hanger.topFlangeOpenClosedAngle"
            warning={hangerType === HANGER_TYPE.ALL_TYPES && parseFloat(topFlangeOpenClosedAngle) !== 0}
          >
            Top Flange Bend (Degrees)
          </FormLabel>
          <RangeSlider
            name="hanger.topFlangeOpenClosedAngle"
            value={topFlangeOpenClosedAngle}
            onChange={handleChangeOpenClosedAngle}
            min={-30}
            max={30}
            addonBefore="Closed"
            addonAfter="Open"
            marks={TOPFLANGE_BEND_MARKS}
            error={errors['hanger.topFlangeOpenClosedAngle']}
            data-testid="hanger.topFlangeOpenClosedAngle"
          />
        </FormGroup>

        <FormGroup
          className="topFlangeSlope"
          warning={hangerType === HANGER_TYPE.ALL_TYPES && parseFloat(topFlangeSlopedDownAngle) !== 0}
        >
          <FormLabel
            htmlFor="hanger.topFlangeSlopedDownAngle"
            warning={hangerType === HANGER_TYPE.ALL_TYPES && parseFloat(topFlangeSlopedDownAngle) !== 0}
          >
            Top Flange Slope (Degrees)
          </FormLabel>

          <RangeSlider
            name="hanger.topFlangeSlopedDownAngle"
            value={topFlangeSlopedDownAngle}
            onChange={handleChangeSlopedDownAngle}
            min={-35}
            max={35}
            addonBefore="Down Left"
            addonAfter="Down Right"
            marks={TOPFLANGE_SLOPE_MARKS}
            error={errors['hanger.topFlangeSlopedDownAngle']}
            data-testid="hanger.topFlangeSlopedDownAngle"
          />
        </FormGroup>
      </div>

      <div className="grid grid__auto--250 grid--gap-0-2">
        <FormGroup warning={invalidOffsetDirection}>
          <FormLabel htmlFor="topFlangeOffset" warning={invalidOffsetDirection}>
            Offset Direction (Top Flange Only)
          </FormLabel>
          <Dropdown
            name="topFlangeOffset"
            options={hanger.offsetType.options}
            value={topFlangeOffset}
            onChange={handleChange}
            data-testid="topFlangeOffset"
          />
        </FormGroup>

        <FormGroup>
          <FormLabel htmlFor="hlcFlush">High, Low, Center Flush</FormLabel>
          <Dropdown
            name="hlcFlush"
            options={hanger.hlcFlush.options}
            value={hlcFlush}
            onChange={handleChange}
            data-testid="hlcFlush"
          />
        </FormGroup>
      </div>
    </React.Fragment>
  )
}

TopFlangeOptions.propTypes = {
  hangerType: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  onValidateField: PropTypes.func.isRequired,
  errors: PropTypes.object.isRequired,
}

export default memo(TopFlangeOptions)
